import { queryOptions, useQuery } from "@tanstack/react-query";
import { unwrap } from "@/lib/api";
import { api } from "./client";
import type { components } from "./schema";

/*
 * `GET /api/v1/me` — die Auskunft über die laufende Sitzung.
 *
 * Der Endpunkt antwortet genau zweierlei (siehe Spec): 200 mit `MeKoerper`
 * oder 401 ohne Body. Ein 403 gibt es hier nicht — wer angemeldet ist, darf
 * seine eigene Sitzung sehen.
 *
 * ═══ `sichten` IST die Navigation ═════════════════════════════════════════
 *
 * `sichten` trägt die Navigationspunkte fertig gefiltert: Pfad und Titel, in
 * Navigations-Reihenfolge, die `admin`-Wildcard schon aufgelöst. Die Karte
 * Sicht → Rollen steht in `internal/auth/navigation.go` (`NavItems`), und
 * DIESELBE Karte gatet die Endpunkte (`verlangeSicht`).
 *
 * Deshalb rechnet die SPA das NICHT aus `rollen` nach. Täte sie es, gäbe es
 * zwei Zugangsregeln: die des Gates und eine stillere im Frontend. Die beiden
 * laufen auseinander, sobald eine Rolle dazukommt — und dann bietet das Menü
 * entweder etwas an, was der Server mit 403 beantwortet, oder verbirgt etwas
 * Erlaubtes. Beides fällt erst im Betrieb auf. `rollen` ist hier Auskunft
 * (z. B. für eine Diagnoseanzeige), keine Berechnungsgrundlage.
 *
 * ═══ `null` ist ein gültiger Wert ═════════════════════════════════════════
 *
 * `rollen` und `sichten` sind im Vertrag `array | null` UND `required`. Go
 * serialisiert die leeren Listen zwar als `[]` (siehe `sichtenFuer`: „Nie
 * nil"), aber der Typ lässt `null` zu — und ein Client, der darauf nicht
 * vorbereitet ist, stürzt beim ersten Vertragsdetail, das sich ändert. Die
 * Anzeige behandelt beide Formen gleich: keine Sicht freigeschaltet.
 *
 * Alle Typen kommen aus der Spec. Benennt das Go-DTO ein Feld um, bricht nach
 * `pnpm codegen` der Build, statt dass der Kopf still leer bleibt.
 */

export type MeKoerper = components["schemas"]["MeKoerper"];
export type Sicht = components["schemas"]["Sicht"];

export const meQueryOptions = queryOptions({
  queryKey: ["me"] as const,
  queryFn: async ({ signal }): Promise<MeKoerper> => unwrap(await api.GET("/api/v1/me", { signal })),
  /*
   * Eine Minute — bewusst zwischen den beiden Extremen, und aus zwei
   * gegenläufigen Gründen:
   *
   * NACH OBEN begrenzt es die Sitzung, nicht die Rolle. Rollen ändert ein
   * Administrator, das ist selten. Die Sitzung dagegen endet von selbst
   * (`auth.SitzungsDauer` = 24 h) oder wird abgemeldet — und danach zeigt ein
   * veralteter Cache eine volle Navigation, deren Punkte ausnahmslos in 401
   * laufen. Ein Kopf, der nach dem Ablauf noch anbietet statt zur Anmeldung
   * zu führen, ist schlechter als einer, der eine Minute hinterherhinkt.
   * Deshalb NICHT die 5 Minuten von `controllingQueryOptions`: dort ist der
   * Preis eines veralteten Werts eine alte Zahl, hier ein toter Klick.
   *
   * NACH UNTEN spart es Anfragen. Die Navigation hängt im Kopf und rendert auf
   * jeder Seite; ohne `staleTime` fragte jeder Seitenwechsel neu, für eine
   * Antwort, die sich stündlich nicht ändert. Eine Minute fasst eine
   * Klickstrecke zu einer Anfrage zusammen.
   *
   * KEINE eigene Retry-Politik: `lib/query-client.ts` unterdrückt bei 4xx
   * schon den Retry. Der 401 ist die gemeinte Antwort des Servers („nicht
   * angemeldet"), kein Aussetzer — zweimal nachfassen verzögert nur den
   * Anmelde-Link. 5xx dürfen dort zweimal nachfassen.
   */
  staleTime: 60_000,
});

/**
 * Sagt, ob der angemeldete Nutzer eine Sicht aufrufen darf.
 *
 * ═══ Gefragt wird `sichten`, NICHT `rollen` ═══════════════════════════════
 *
 * Aus genau dem Grund, der oben im Dateikopf steht: `rollen` ist Auskunft,
 * keine Berechnungsgrundlage. `sichten` ist die Antwort DESSELBEN Servers,
 * der die Endpunkte gatet (`NavItems` → `verlangeSicht`) — die Wildcard
 * `admin` ist darin schon aufgelöst. Ein `rollen.includes("suche")` wäre die
 * zweite, stillere Zugangsregel, vor der der Dateikopf warnt; ein Blick in
 * `sichten` ist keine.
 *
 * ═══ Beim Laden gilt „nicht erlaubt" ══════════════════════════════════════
 *
 * `data` ist undefined, solange die Anfrage läuft — und dann ist die Antwort
 * `false`, der Aufrufer zeigt also nichts an. Das ist die richtige Richtung:
 * ein Link, der erst da ist und dann verschwindet, wurde womöglich schon
 * angeklickt. Umgekehrt kostet es fast nie etwas, weil `Navigation.tsx`
 * dieselbe Anfrage auf jeder Seite stellt und `staleTime` sie eine Minute
 * hält — der Cache ist beim Rendern der Sicht praktisch immer warm.
 *
 * Der Pfad ist derselbe String wie in `auth.NavItems` (dort die
 * `Sicht*`-Konstanten) und wie im Routenbaum.
 */
export function useSichtErlaubt(pfad: string): boolean {
  const { data } = useQuery(meQueryOptions);
  return (data?.sichten ?? []).some((sicht) => sicht.pfad === pfad);
}
